/**
 * Human-in-the-Loop (HITL) Contract
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 5 (Human-in-the-Loop)
 *
 * Defines interfaces for human approval workflows:
 * - Approval requests for write operations
 * - Approval status tracking
 * - Risk assessment
 * - Audit logging
 *
 * @see https://docs.anthropic.com/en/docs/agents-and-agentic-workflows/human-in-the-loop
 */

import { z } from 'zod';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Threshold for considering an operation as "bulk" (affecting multiple records).
 * Operations affecting more than this number of records trigger higher risk scoring.
 */
export const BULK_OPERATION_THRESHOLD = 10;

// ============================================================================
// ENUMS
// ============================================================================

export enum ApprovalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  AUTO_APPROVED = 'auto_approved', // Below risk threshold
}

export enum RiskLevel {
  LOW = 'LOW', // Read-only, no side effects
  MEDIUM = 'MEDIUM', // Reversible changes
  HIGH = 'HIGH', // Irreversible changes
  CRITICAL = 'CRITICAL', // Financial/security impact
}

export enum OperationType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  EXECUTE = 'EXECUTE', // Tool/action execution
  TRANSFER = 'TRANSFER', // Data transfer
  EXPORT = 'EXPORT', // Data export
}

// ============================================================================
// CORE TYPES
// ============================================================================

/**
 * Approval request for a write operation
 */
export interface ApprovalRequest {
  id: string;
  businessId: string;
  userId: string;
  sessionId: string;
  agentId: string;
  operation: OperationType;
  resource: string; // What is being modified
  resourceId?: string; // ID of specific resource
  action: string; // Human-readable description
  payload: unknown; // The actual operation payload
  riskLevel: RiskLevel;
  riskFactors: string[];
  status: ApprovalStatus;
  requestedAt: Date;
  expiresAt: Date;
  decidedAt?: Date;
  decidedBy?: string;
  decisionReason?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Risk assessment result
 */
export interface RiskAssessment {
  level: RiskLevel;
  score: number; // 0.0 - 1.0
  factors: string[];
  requiresApproval: boolean;
  suggestedTimeout: number; // Seconds
  impactSummary: string;
}

/**
 * Approval decision
 */
export interface ApprovalDecision {
  requestId: string;
  approved: boolean;
  decidedBy: string;
  reason?: string;
  modifiedPayload?: unknown; // If approver modified the operation
}

/**
 * Operation result after approval
 */
export interface OperationResult {
  requestId: string;
  success: boolean;
  result?: unknown;
  error?: string;
  executedAt: Date;
}

// ============================================================================
// REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Create approval request
 */
export interface CreateApprovalRequest {
  businessId: string;
  userId: string;
  sessionId: string;
  agentId: string;
  operation: OperationType;
  resource: string;
  resourceId?: string;
  action: string;
  payload: unknown;
  timeoutSeconds?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Create approval response
 */
export interface CreateApprovalResponse {
  request: ApprovalRequest;
  riskAssessment: RiskAssessment;
  waitForDecision: boolean;
}

/**
 * Submit decision request
 */
export interface SubmitDecisionRequest {
  businessId: string;
  requestId: string;
  approved: boolean;
  decidedBy: string;
  reason?: string;
  modifiedPayload?: unknown;
}

/**
 * Submit decision response
 */
export interface SubmitDecisionResponse {
  request: ApprovalRequest;
  operationResult?: OperationResult;
}

/**
 * Get pending approvals request
 */
export interface GetPendingApprovalsRequest {
  businessId: string;
  userId?: string;
  agentId?: string;
  limit?: number;
}

/**
 * Get pending approvals response
 */
export interface GetPendingApprovalsResponse {
  requests: ApprovalRequest[];
  total: number;
}

/**
 * Get approval history request
 */
export interface GetApprovalHistoryRequest {
  businessId: string;
  userId?: string;
  agentId?: string;
  status?: ApprovalStatus[];
  fromDate?: Date;
  toDate?: Date;
  limit?: number;
  offset?: number;
}

/**
 * Get approval history response
 */
export interface GetApprovalHistoryResponse {
  requests: ApprovalRequest[];
  total: number;
  hasMore: boolean;
}

/**
 * Cancel approval request
 */
export interface CancelApprovalRequest {
  businessId: string;
  requestId: string;
  reason?: string;
}

/**
 * Cancel approval response
 */
export interface CancelApprovalResponse {
  request: ApprovalRequest;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface HITLConfig {
  enabled: boolean;
  defaultTimeoutSeconds: number;
  autoApproveThreshold: number; // Risk score below which auto-approve
  requireApprovalFor: OperationType[];
  highRiskResources: string[];
  exemptUsers: string[]; // Users who don't need approval
  maxPendingPerUser: number;
}

export const DEFAULT_HITL_CONFIG: HITLConfig = {
  enabled: true,
  defaultTimeoutSeconds: 300, // 5 minutes
  autoApproveThreshold: 0.3, // Auto-approve if risk < 30%
  requireApprovalFor: [OperationType.DELETE, OperationType.TRANSFER, OperationType.EXPORT],
  highRiskResources: ['payment', 'user', 'subscription', 'case'],
  exemptUsers: [],
  maxPendingPerUser: 10,
};

// ============================================================================
// RISK SCORING RULES
// ============================================================================

export interface RiskRule {
  name: string;
  condition: (request: CreateApprovalRequest) => boolean;
  score: number;
  level: RiskLevel;
  reason: string;
}

export const DEFAULT_RISK_RULES: RiskRule[] = [
  {
    name: 'delete_operation',
    condition: (r) => r.operation === OperationType.DELETE,
    score: 0.7,
    level: RiskLevel.HIGH,
    reason: 'Delete operations are irreversible',
  },
  {
    name: 'financial_resource',
    condition: (r) =>
      ['payment', 'subscription', 'invoice', 'billing'].some((t) => r.resource.toLowerCase().includes(t)),
    score: 0.8,
    level: RiskLevel.CRITICAL,
    reason: 'Operation affects financial data',
  },
  {
    name: 'bulk_operation',
    condition: (r) => {
      const payload = r.payload as Record<string, unknown>;
      return Array.isArray(payload?.ids) && (payload.ids as unknown[]).length > BULK_OPERATION_THRESHOLD;
    },
    score: 0.6,
    level: RiskLevel.HIGH,
    reason: 'Bulk operation affecting multiple records',
  },
  {
    name: 'user_data',
    condition: (r) => r.resource.toLowerCase().includes('user') || r.resource.toLowerCase().includes('account'),
    score: 0.5,
    level: RiskLevel.MEDIUM,
    reason: 'Operation affects user data',
  },
  {
    name: 'export_operation',
    condition: (r) => r.operation === OperationType.EXPORT,
    score: 0.5,
    level: RiskLevel.MEDIUM,
    reason: 'Data export may contain sensitive information',
  },
];

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const operationTypeSchema = z.nativeEnum(OperationType);
export const riskLevelSchema = z.nativeEnum(RiskLevel);
export const approvalStatusSchema = z.nativeEnum(ApprovalStatus);

export const createApprovalRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  sessionId: z.string().min(1, 'Session ID is required'),
  agentId: z.string().min(1, 'Agent ID is required'),
  operation: operationTypeSchema,
  resource: z.string().min(1, 'Resource is required'),
  resourceId: z.string().optional(),
  action: z.string().min(1, 'Action description is required'),
  payload: z.unknown(),
  timeoutSeconds: z.number().min(30).max(3600).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const submitDecisionRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  requestId: z.string().min(1, 'Request ID is required'),
  approved: z.boolean(),
  decidedBy: z.string().min(1, 'Decider ID is required'),
  reason: z.string().optional(),
  modifiedPayload: z.unknown().optional(),
});

export const getPendingApprovalsRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().optional(),
  agentId: z.string().optional(),
  limit: z.number().min(1).max(100).optional().default(20),
});

export const getApprovalHistoryRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().optional(),
  agentId: z.string().optional(),
  status: z.array(approvalStatusSchema).optional(),
  fromDate: z.date().optional(),
  toDate: z.date().optional(),
  limit: z.number().min(1).max(100).optional().default(50),
  offset: z.number().min(0).optional().default(0),
});

export const cancelApprovalRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  requestId: z.string().min(1, 'Request ID is required'),
  reason: z.string().optional(),
});
